<template>
  <div :class="['home flex-column', programStyle]">
    <div class="title-container flex-row">
      <div class="left flex-column">
        <span class="title">{{ $t('forms') }}</span>
      </div>
    </div>
    <div class="content flex-column" v-if="!addForm">
      <div class="above-table flex-column">
        <span v-if="formTypeOptions && formTypeOptions.length === 0">{{ $t('no-forms-for-domain') }}</span>
        <div class="table-descriptors flex-row">
          <span>{{ $t('form-records') }}</span>
          <button v-if="formTypeOptions && formTypeOptions.length > 0" @click="newForm">{{ $t('add-form') }}</button>
        </div>
      </div>
      <div class="table-container flex-column">
        <DataTable :headings="formConfigTH" :rows="formConfigs" :perPage="numPerPage" :page="currentPage">
          <template v-slot:statusSlot="slotProps">
            <div v-if="slotProps.row.isExpired">{{ $t('expired') }}</div>
            <div v-else-if="slotProps.row.isActive === 1">{{ $t('active') }}</div>
            <div v-else-if="slotProps.row.isActive === 0">{{ $t('draft') }}</div>
          </template>
          <template v-slot:viewSlot="slotProps">
            <Eyecon class="eyecon" @click="viewForm(slotProps.row)"></Eyecon>
          </template>
          <template v-slot:copySlot="slotProps">
            <Copy class="copy-icon" @click="copyForm(slotProps.row)"></Copy>
          </template>
          <template v-slot:deleteSlot="slotProps">
            <Delete class="delete-icon" @click="openModal(slotProps.row)"></Delete>
          </template>
        </DataTable>
        <div class="row-container">
          <GPagination v-if="pageCount > 1" class="pagination" v-model="currentPage" :pageCount="pageCount" />
        </div>
      </div>
    </div>
    <div class="content flex-column above-table" v-if="addForm">
      <span class="sub-title">{{ $t('add-form') }}</span>
      <span class="section-title">* form details:</span>
      <div class="flexRow">
        <Multiselect
          v-model="formConfig.formType"
          :options="formTypeOptions"
          class="multiselect single"
          :placeholder="$t('form-type')"
          track-by="formType"
          label="formType"
          :showLabels="false"
          :allow-empty="false"
          @input="onUpdateType"
        ></Multiselect>
        <Multiselect
          v-if="formLayoutOptions.length > 0"
          v-model="formConfig.formLayout"
          :options="formLayoutOptions"
          class="multiselect single form-layout"
          :placeholder="$t('form-layout')"
          track-by="layout"
          label="layout"
          :showLabels="false"
          :allow-empty="false"
          @input="getFormInfo"
        ></Multiselect>
        <input v-model="formConfig.formName" :placeholder="$t('form-name')" />
      </div>
      <div class="flexRow schedule">
        <span class="section-title">* {{ $t('schedule') }} <span>:</span></span>
        <Multiselect
          v-if="formConfig.formType.configType === 'multi-phase'"
          v-model="formConfig.period"
          :options="timePeriodOptions"
          class="multiselect single"
          :placeholder="$t('time-period')"
          track-by="period"
          label="period"
          :showLabels="false"
          :allow-empty="false"
        ></Multiselect>
        <div class="flex-row" v-if="formConfig.formType.configType === 'simple'">
          <Collapsible
            class="start-date"
            :title="startDate ? dateFilter(startDate, 'calendarDate') : 'Start Date'"
            :canToggle="true"
            :contentAbsolute="true"
          >
            <v-date-picker
              v-model="startDate"
              mode="dateTime"
              :model-config="modelConfig"
              is24hr
              :popover="{ placement: 'right', visibility: 'click' }"
              :min-date="newDate"
              :input-props="{ class: 'w-full', placeholder: newDate, readonly: true }"
            />
          </Collapsible>
          <span class="between">{{ $t('to') }}</span>
          <Collapsible
            :title="endDate ? dateFilter(endDate, 'calendarDate') : 'End Date'"
            :canToggle="true"
            :contentAbsolute="true"
            :invalid="!dateValidation(startDate, endDate)"
          >
            <v-date-picker
              v-model="endDate"
              mode="dateTime"
              :model-config="modelConfig"
              is24hr
              :popover="{ placement: 'right', visibility: 'click' }"
              :min-date="newDate"
              :input-props="{ class: 'w-full', placeholder: newDate, readonly: true }"
            />
          </Collapsible>
        </div>
      </div>
      <div class="flexColumn phases">
        <span class="section-title">* Phases:</span>
        <div v-for="(phase, index) in formConfig.formType.phases" :key="phase.phaseName" class="flex-column phase">
          <span class="small-title"> {{ index + 1 }}: {{ phase.phaseName }}</span>
          <div class="flex-row dates" v-if="formConfig.formType.configType === 'multi-phase'">
            <Collapsible
              :title="phase.startDate ? dateFilter(phase.startDate, 'calendarDate') : 'Start Date'"
              :canToggle="true"
              :contentAbsolute="true"
            >
              <v-date-picker
                v-model="phase.startDate"
                mode="dateTime"
                :model-config="modelConfig"
                is24hr
                :popover="{ placement: 'right', visibility: 'click' }"
                :min-date="newDate"
                :input-props="{ class: 'w-full', placeholder: newDate, readonly: true }"
              />
            </Collapsible>
            <span class="between">{{ $t('to') }}</span>
            <Collapsible
              :title="phase.endDate ? dateFilter(phase.endDate, 'calendarDate') : 'End Date'"
              :canToggle="true"
              :contentAbsolute="true"
              :invalid="!dateValidation(phase.startDate, phase.endDate)"
            >
              <v-date-picker
                v-model="phase.endDate"
                mode="dateTime"
                :model-config="modelConfig"
                is24hr
                :popover="{ placement: 'right', visibility: 'click' }"
                :min-date="newDate"
                :input-props="{ class: 'w-full', placeholder: newDate, readonly: true }"
              />
            </Collapsible>
          </div>
          <div v-if="formConfig.formType.configType === 'simple'" class="flex-row roles-list">
            <span class="role-description">Roles:</span>
            <div v-for="(role, index) in phase.roles" :key="index" class="flex-row roles">
              {{ role }}
            </div>
          </div>
        </div>
      </div>
      <div class="flex-row slider-container">
        <span :class="[formConfig.isActive === 0 ? 'bold' : '']">DRAFT</span>
        <GSwitch :checked="formConfig.isActive === 1" class="slider" @updated="togglePublish"></GSwitch>
        <span :class="[formConfig.isActive === 1 ? 'bold' : '']">PUBLISH</span>
      </div>
      <div class="flex-row button-container">
        <button v-if="addForm === 'new'" :class="['add', submitValidation ? '' : 'disabled']" @click="submitFormConfig">
          {{ $t('add-form') }}
        </button>
        <button
          v-if="addForm === 'edit'"
          :class="['add', submitValidation ? '' : 'disabled']"
          @click="submitFormConfig"
          :disabled="!submitValidation && !loading"
        >
          {{ $t('save') }}
        </button>
        <button @click="clearForm">{{ $t('cancel') }}</button>
      </div>
    </div>
    <Modal class="modal-screens" v-show="isModalOpen">
      <template v-slot:body class="body">
        {{ $t('form-delete-warning') }}
        <div class="modal-button-container">
          <button class="submit" @click="deleteForm">{{ $t('submit') }}</button>
          <button @click="isModalOpen = false">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import DataTable from '@/components/DataTable.vue';
import GPagination from '@/components/GComponents/GPagination.vue';
import Collapsible from '@/components/Collapsible.vue';
import Multiselect from 'vue-multiselect';
import GSwitch from '@/components/GComponents/GSwitch.vue';
import Modal from '@/components/Modal.vue';
import Eyecon from '@/assets/new/eye-con.svg';
import Copy from '@/assets/new/icon_copy.svg';
import Delete from '@/assets/new/icon_trash.svg';
import { mapState } from 'vuex';
import formConfigTH from '@/utils/tableHeaders/formConfigTH';

import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  name: 'CCForms',
  components: {
    DataTable,
    Multiselect,
    Collapsible,
    GSwitch,
    GPagination,
    Eyecon,
    Copy,
    Delete,
    Modal,
  },
  data() {
    return {
      numPerPage: 10,
      currentPage: 1,
      addForm: false,
      newDate: new Date(),
      formTypeOptions: null,
      timePeriodOptions: [],
      startDate: null,
      endDate: null,
      formConfigs: null,
      formConfig: {
        id: null,
        domain: this.programName,
        period: null,
        formType: null,
        formLayout: null,
        formName: null,
        isActive: 0,
      },
      modelConfig: {
        type: 'string',
        timeAdjust: '00:00',
      },
      publish: 'draft',
      isModalOpen: false,
      tempRow: null,
      formConfigTH: formConfigTH(),
      loader: null,
    };
  },
  methods: {
    // For Simple Configs with no designated period, use the set time frame within the phases.
    setFormConfigs(resp) {
      this.startDate = null;
      this.endDate = null;

      if (resp) {
        resp.forEach((result) => {
          if (result.period === null && result.configType === 'simple') {
            if (result.phases.length > 0) {
              const startDate = this.$options.filters.date(result.phases[0].startDate);
              const endDate = this.$options.filters.date(result.phases[0].endDate);
              result.period = `${startDate} - ${endDate}`;
            }
          }
        });
      }
      this.formConfigs = resp;
    },
    setFormTypeOptions(resp) {
      this.formTypeOptions = resp;
    },
    setTimePeriodOptions(resp) {
      this.timePeriodOptions = resp;
    },
    getFormConfigs() {
      const domain = this.programName;

      return this.$store.dispatch('getFormConfigs', { domain }).then((res) => {
        this.setFormConfigs(res);
      });
    },
    getFormInfo() {
      const loader = this.$loading.show();

      const domain = this.programName;

      let { layout } = this.formConfig.formLayout;

      if (layout === 'Essential Tools & Required Service Equipment') {
        layout = encodeURIComponent('Essential Tools & Required Service Equipment');
      }

      this.$store
        .dispatch('getCCFormTypes', { domain, layout })
        .then((res) => {
          this.setUpRoles(res);
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    setUpRoles(res) {
      this.removeRoles();
      res.forEach((form) => {
        if (this.formConfig.formType.formType === form.formType) {
          this.formConfig.formType.phases = form.phases;
        }
      });
    },
    getAll() {
      const loader = this.$loading.show();

      const domain = this.programName;

      const layout = '';

      Promise.all([
        this.$store.dispatch('getFormConfigs', { domain }),
        this.$store.dispatch('getCCFormTypes', { domain, layout }),
        this.$store.dispatch('getAllPeriods', { domain }),
      ])
        .then((resp) => {
          this.setFormConfigs(resp[0]);
          this.setFormTypeOptions(resp[1]);
          this.setTimePeriodOptions(resp[2]);
        })
        .finally(() => {
          loader.hide();
        });
    },
    openModal(data) {
      this.tempRow = data;
      this.isModalOpen = true;
    },
    togglePublish() {
      if (this.formConfig.isActive === 0) {
        this.formConfig.isActive = 1;
      } else {
        this.formConfig.isActive = 0;
      }
    },
    newForm() {
      const formType = JSON.parse(JSON.stringify(this.formTypeOptions[0]));

      this.addForm = 'new';
      this.formConfig = {
        id: null,
        domain: this.programName,
        period: null,
        formType,
        formName: null,
        formLayout: null,
        isActive: 0,
      };
    },
    copyForm(form) {
      const loader = this.$loading.show();

      let { period } = form;

      if (form.configType === 'simple') {
        period = null;
        this.startDate = form.phases[0].startDate;
        this.endDate = form.phases[0].endDate;
      }

      const params = {
        id: null,
        configType: form.configType,
        domain: form.domain,
        period,
        formType: form.formType,
        layout: form.layout,
        name: `${form.name}(copy)`,
        isActive: 0,
        phases: form.phases,
        startDate: this.startDate,
        endDate: this.endDate,
      };

      this.$store
        .dispatch('submitFormConfig', params)
        .then((res) => {
          if (res) {
            this.getFormConfigs();
            loader.hide();
          }
        })
        .catch(() => {
          loader.hide();
        });
    },
    submitFormConfig() {
      this.loader = this.$loading.show();
      let period;
      if (this.formConfig.formType.configType === 'multi-phase') {
        ({ period } = this.formConfig.period);
      }

      const params = {
        id: this.formConfig.id,
        domain: this.programName,
        period,
        formType: this.formConfig.formType.formType,
        layout: this.formConfig.formLayout.layout,
        name: this.formConfig.formName,
        isActive: this.formConfig.isActive,
        configType: this.formConfig.formType.configType,
        startDate: this.startDate,
        endDate: this.endDate,
        phases: this.formConfig.formType.phases,
      };

      this.$store
        .dispatch('submitFormConfig', params)
        .then((res) => {
          if (res) {
            let message = '';
            if (this.addForm === 'new') {
              message = this.$t('form-successfully-added');
            } else {
              message = this.$t('form-successfully-updated');
            }
            this.$notify({
              group: 'primary',
              type: 'success',
              text: message,
            });
            this.getFormConfigs();
            this.addForm = false;
          }
        })
        .catch((error) => {
          this.$notify({
            group: 'primary',
            type: 'error',
            text: error.data.error,
          });
        })
        .finally(() => {
          this.loader.hide();
        });
    },
    viewForm(form) {
      this.addForm = 'edit';

      const index = this.timePeriodOptions.findIndex((item) => item.period === form.period);

      this.formConfig = {
        id: form.id,
        domain: form.domain,
        period: this.timePeriodOptions[index],
        formType: {
          formType: form.formType,
          configType: form.configType,
          phases: form.phases,
        },
        formLayout: null,
        formName: form.name,
        isActive: form.isActive,
      };
      // Form Config Form Type needs to exist before searching which layout was chosen.

      const layout = this.formLayoutOptions.findIndex((item) => item.layout === form.layout);

      this.formConfig.formLayout = this.formLayoutOptions[layout];

      this.startDate = form.phases[0].startDate;
      this.endDate = form.phases[0].endDate;
    },
    deleteForm() {
      const loader = this.$loading.show();

      const { id } = this.tempRow;

      this.$store.dispatch('deleteFormConfig', id).then((res) => {
        if (res) {
          this.getFormConfigs();
          this.isModalOpen = false;
          loader.hide();
        }
      });
    },
    dateFilter(value, filter) {
      if (value && filter && this.$options.filters[filter]) {
        return this.$options.filters[filter](value);
      }
      return value;
    },
    dateValidation(startDate, endDate) {
      if (startDate > endDate) {
        return false;
      }
      return true;
    },
    onUpdateType() {
      this.formConfig.formLayout = null;
    },
    clearForm() {
      this.removeRoles();
      this.startDate = null;
      this.endDate = null;
      this.addForm = false;
    },
    removeRoles() {
      this.formConfig.formType.phases.forEach((phase) => {
        phase.roles = [];
      });
    },
  },
  computed: {
    ...mapState('global', ['programName', 'programStyle']),
    submitValidation() {
      if (this.formConfig.formType.configType === 'simple') {
        if (this.startDate && this.endDate && this.formConfig.formName && this.formConfig.formLayout) {
          return this.dateValidation(this.startDate, this.endDate);
        }
      } else if (this.formConfig.formName && this.formConfig.formLayout && this.formConfig.period) {
        return this.formConfig.formType.phases.every(
          (phase) => phase.startDate && phase.endDate && this.dateValidation(phase.startDate, phase.endDate),
        );
      }
      return false;
    },
    formLayoutOptions() {
      if (this.formConfig.formType) {
        const index = this.formTypeOptions.findIndex((item) => item.formType === this.formConfig.formType.formType);
        return this.formTypeOptions[index].formLayouts;
      }
      return null;
    },
    pageCount() {
      if (this.formConfigs) {
        const l = this.formConfigs.length;
        const s = this.numPerPage;
        return Math.ceil(l / s);
      }
      return 0;
    },
  },
  created() {
    this.getAll();
  },
};
</script>

<style lang="scss" scoped>
.home {
  width: 100%;
  .title {
    font-size: 32px;
    font-weight: 100;
    text-transform: uppercase;
  }
  .subtitle {
    font-size: 14px;
    margin-top: 5px;
    display: flex;
  }
}
.content {
  height: 100%;
  flex-wrap: wrap;
  margin-top: 50px;
  .sub-title {
    text-align: left;
    font-size: 18px;
    line-height: 24px;
    text-transform: uppercase;
    font-weight: bold;
    margin-bottom: 30px;
  }
  .flexRow {
    display: flex;
    margin-top: 20px;
    align-items: center;
    input {
      margin-left: 20px;
    }
  }
  .flex-column {
    display: flex;
    flex-direction: column;
    text-align: left;
  }
  .small-title {
    color: black;
    font-size: 20px;
    margin-right: 20px;
  }
  .schedule {
    margin-top: 20px;
    align-items: center;
  }
  .phases {
    margin-top: 20px;
    color: black;
    text-align: left;
    .phase {
      margin: 20px 0;
    }
  }
  .between {
    margin: 0 20px;
  }
  .slider-container {
    color: black;
    font-size: 20px;
    margin-top: 20px;
    .bold {
      font-weight: bold;
    }
    .slider {
      margin: 0 10px;
    }
  }
  .button-container {
    margin: 40px 0;
    button {
      width: 150px;
    }
    .add {
      margin-right: 10px;
    }
  }
}
.above-table {
  padding: 0 40px;
}
.title-container {
  text-align: left;
  border-bottom: 1px #dfe4e8 solid;
  padding-left: 40px;
  padding-bottom: 25px;
  padding: 30px 50px 30px 30px;
  justify-content: space-between;
}
.table-descriptors {
  justify-content: space-between;
  // margin-top: 50px;
  font-size: 24px;
  align-items: center;
  .right {
    display: flex;
    input {
      margin-right: 20px;
    }
  }
  span {
    font-size: 18px;
    line-height: 24px;
    text-transform: uppercase;
    font-weight: bold;
  }
}
.role-description {
  margin-right: 10px;
}
.roles {
  &:not(:last-child)::after {
    content: ',';
    margin-right: 5px;
  }
}

.table-container {
  margin-top: 30px;
}
.flex-row {
  align-items: center;
}
::v-deep {
  .filter {
    width: 200px !important;
  }
  .collapsible {
    padding-right: 0px !important;
  }
}
.dates {
  margin-top: 10px;
}
.modal-button-container {
  display: flex;
  margin-top: 10px;
  justify-content: center;
  .submit {
    margin-right: 20px;
  }
}
.form-layout {
  margin-left: 10px;
}
.section-title {
  font-size: 15px;
  line-height: 21px;
  text-transform: uppercase;
  font-weight: bold;
  color: $primary-black;
  margin-right: 15px;
  text-align: left;
}
.roles-list {
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
}
</style>
